<template>
  <div class="min-h-screen bg-gray-50 dark:bg-gray-900 p-6">
    <div class="flex flex-col gap-4 mb-6 lg:flex-row lg:items-center lg:justify-between">
      <div>
        <h1 class="text-3xl font-bold text-gray-900 dark:text-gray-100">租户管理</h1>
        <p class="mt-1 text-sm text-gray-500 dark:text-gray-400">管理租户品牌信息、域名绑定、应用开关与登录方式</p>
      </div>
      <div class="flex items-center gap-3">
        <button
          @click="loadTenants"
          :disabled="loading"
          class="px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          <span class="inline-flex items-center gap-2">
            <ArrowPathIcon :class="loading ? 'animate-spin' : ''" class="w-4 h-4" />
            刷新列表
          </span>
        </button>
        <button @click="handleCreateTenant" class="primary-btn flex items-center gap-2">
          <PlusIcon class="w-5 h-5" />
          新建租户
        </button>
      </div>
    </div>

    <div class="grid grid-cols-1 gap-4 mb-6 md:grid-cols-3">
      <div class="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-5">
        <p class="text-sm text-gray-500 dark:text-gray-400">租户总数</p>
        <p class="mt-2 text-3xl font-bold text-gray-900 dark:text-gray-100">{{ pagination.total }}</p>
      </div>
      <div class="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-5">
        <p class="text-sm text-gray-500 dark:text-gray-400">当前页启用租户</p>
        <p class="mt-2 text-3xl font-bold text-emerald-600 dark:text-emerald-400">{{ activeTenantCount }}</p>
      </div>
      <div class="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-5">
        <p class="text-sm text-gray-500 dark:text-gray-400">默认租户</p>
        <p class="mt-2 text-lg font-semibold text-gray-900 dark:text-gray-100 truncate">{{ defaultTenantName }}</p>
      </div>
    </div>

    <div class="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 overflow-hidden">
      <div class="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
        <div>
          <h2 class="text-lg font-semibold text-gray-900 dark:text-gray-100">租户列表</h2>
          <p class="text-sm text-gray-500 dark:text-gray-400 mt-1">列表接口支持分页，当前每页 {{ pagination.limit }} 条</p>
        </div>
      </div>

      <div v-if="loading" class="py-16 flex justify-center items-center">
        <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>

      <div v-else-if="tenants.length === 0" class="py-16 px-6 text-center">
        <div class="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-indigo-50 dark:bg-indigo-900/40 text-indigo-600 dark:text-indigo-300">
          <BuildingOffice2Icon class="w-7 h-7" />
        </div>
        <h3 class="mt-4 text-lg font-semibold text-gray-900 dark:text-gray-100">暂无租户</h3>
        <p class="mt-2 text-sm text-gray-500 dark:text-gray-400">点击右上角“新建租户”开始配置域名与品牌信息</p>
      </div>

      <div v-else class="overflow-x-auto">
        <table class="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
          <thead class="bg-gray-50 dark:bg-gray-900/60">
            <tr>
              <th class="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">租户</th>
              <th class="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">域名绑定</th>
              <th class="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">启用应用</th>
              <th class="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">登录方式</th>
              <th class="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">状态</th>
              <th class="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">更新时间</th>
              <th class="px-6 py-3 text-right text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">操作</th>
            </tr>
          </thead>
          <tbody class="divide-y divide-gray-200 dark:divide-gray-700">
            <tr v-for="tenant in tenants" :key="tenant.id" class="align-top hover:bg-gray-50 dark:hover:bg-gray-900/40 transition-colors">
              <td class="px-6 py-4">
                <div class="flex flex-col gap-2">
                  <div class="flex items-center gap-2 flex-wrap">
                    <span class="text-sm font-semibold text-gray-900 dark:text-gray-100">{{ tenant.name }}</span>
                    <span
                      v-if="tenant.isDefault"
                      class="inline-flex items-center rounded-full bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300 px-2.5 py-0.5 text-xs font-medium"
                    >
                      默认租户
                    </span>
                    <span
                      :class="tenant.isActive ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300' : 'bg-gray-200 text-gray-700 dark:bg-gray-700 dark:text-gray-200'"
                      class="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium"
                    >
                      {{ tenant.isActive ? '已启用' : '已停用' }}
                    </span>
                  </div>
                  <div class="space-y-1 text-xs text-gray-500 dark:text-gray-400">
                    <p>租户 ID: {{ tenant.id }}</p>
                    <p>创建时间: {{ formatDateTime(tenant.createdAt) }}</p>
                  </div>
                </div>
              </td>
              <td class="px-6 py-4">
                <div class="flex flex-wrap gap-2 max-w-xs">
                  <span
                    v-for="host in tenant.hosts"
                    :key="host.id"
                    class="inline-flex items-center rounded-md bg-sky-50 text-sky-700 dark:bg-sky-900/40 dark:text-sky-300 px-2.5 py-1 text-xs font-medium break-all"
                  >
                    {{ host.host }}
                  </span>
                </div>
              </td>
              <td class="px-6 py-4">
                <div class="flex flex-wrap gap-2 max-w-xs">
                  <span
                    v-for="app in tenant.enabledApps"
                    :key="app"
                    class="inline-flex items-center rounded-full bg-indigo-50 text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-300 px-2.5 py-1 text-xs font-medium"
                  >
                    {{ TENANT_ENABLED_APP_LABELS[app] }}
                  </span>
                  <span
                    v-if="tenant.enabledApps.length === 0"
                    class="text-sm text-gray-400 dark:text-gray-500"
                  >
                    未配置
                  </span>
                </div>
              </td>
              <td class="px-6 py-4">
                <div class="flex flex-wrap gap-2 max-w-xs">
                  <span
                    v-for="method in tenant.loginMethods"
                    :key="method"
                    class="inline-flex items-center rounded-full bg-emerald-50 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300 px-2.5 py-1 text-xs font-medium"
                  >
                    {{ TENANT_LOGIN_METHOD_LABELS[method] }}
                  </span>
                  <span
                    v-if="tenant.loginMethods.length === 0"
                    class="text-sm text-gray-400 dark:text-gray-500"
                  >
                    未配置
                  </span>
                </div>
              </td>
              <td class="px-6 py-4">
                <div class="flex flex-col gap-1">
                  <div class="flex items-center gap-2">
                    <span class="inline-flex items-center rounded-full bg-indigo-50 text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-300 px-2.5 py-1 text-xs font-medium">
                      主域名
                    </span>
                    <span class="text-sm font-medium text-gray-900 dark:text-gray-100">
                      {{ tenant.hosts[0]?.host || '未配置' }}
                    </span>
                  </div>

                  <div v-for="host in tenant.hosts.slice(1)" :key="host.id" class="text-sm text-gray-600 dark:text-gray-400">
                    🔗 {{ host.host }}
                  </div>
                </div>
              </td>
              <td class="px-6 py-4">
                  <div class="space-y-2 text-sm text-gray-600 dark:text-gray-300">
                    <p>域名数: {{ tenant.hosts.length }}</p>
                    <p>默认状态: {{ tenant.isDefault ? '是' : '否' }}</p>
                    <p>主页模式: {{ TENANT_HOMEPAGE_MODE_LABELS[tenant.homepageMode] }}</p>
                    <p v-if="tenant.isDemo" class="text-amber-600 dark:text-amber-400 font-medium">演示租户</p>
                  </div>
                </td>
              <td class="px-6 py-4 text-sm text-gray-600 dark:text-gray-300 whitespace-nowrap">
                {{ formatDateTime(tenant.updatedAt) }}
              </td>
              <td class="px-6 py-4">
                <div class="flex items-center justify-end gap-2">
                  <button
                    @click="handleEditTenant(tenant.id)"
                    class="inline-flex items-center gap-1 rounded-lg px-3 py-2 text-sm font-medium text-indigo-700 dark:text-indigo-300 bg-indigo-50 dark:bg-indigo-900/30 hover:bg-indigo-100 dark:hover:bg-indigo-900/50 transition-colors"
                  >
                    <PencilSquareIcon class="w-4 h-4" />
                    编辑
                  </button>
                  <button
                    @click="openTeamModal(tenant)"
                    class="inline-flex items-center gap-1 rounded-lg px-3 py-2 text-sm font-medium text-sky-700 dark:text-sky-300 bg-sky-50 dark:bg-sky-900/30 hover:bg-sky-100 dark:hover:bg-sky-900/50 transition-colors"
                  >
                    <UserGroupIcon class="w-4 h-4" />
                    团队
                  </button>
                  <button
                    @click="handleDeleteTenant(tenant)"
                    :disabled="submitting"
                    class="inline-flex items-center gap-1 rounded-lg px-3 py-2 text-sm font-medium text-red-700 dark:text-red-300 bg-red-50 dark:bg-red-900/30 hover:bg-red-100 dark:hover:bg-red-900/50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    <TrashIcon class="w-4 h-4" />
                    删除
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <Pagination
        v-if="pagination.total > 0"
        :total="pagination.total"
        v-model:current-page="pagination.page"
        v-model:page-size="pagination.limit"
        :show-jumper="false"
        @update:current-page="loadTenants"
        @update:page-size="loadTenants"
      />
    </div>

    <Modal :show="showTenantModal" @close="closeTenantModal" :title="modalTitle" size="large">
      <div class="border-b border-gray-200 dark:border-gray-700">
        <nav class="-mb-px  flex space-x-6 px-6" aria-label="Tabs">
          <button
            v-for="tab in tabs"
            :key="tab.id"
            @click="activeTab = tab.id"
            :class="[
              activeTab === tab.id
                ? 'border-indigo-500 text-indigo-600'
                : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700',
              'whitespace-nowrap border-b-2 py-4 px-1 text-sm font-medium',
            ]"
          >
            {{ tab.label }}
          </button>
        </nav>
      </div>

      <form class="p-6" @submit.prevent="handleSubmitTenant">
        <!-- Fixed height container to prevent jumping when switching tabs -->
        <div class="min-h-[500px] max-h-[400px] space-y-6 overflow-y-auto">
        <div v-show="activeTab === 'basic'">
          <div class="grid grid-cols-1 gap-5 md:grid-cols-2">
            <div class="md:col-span-2">
              <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">站点名称</label>
              <p class="mb-2 text-xs text-gray-500 dark:text-gray-400">用户访问网站时看到的名称，也会用于浏览器标题等页面名称。</p>
              <input
                v-model="form.name"
                type="text"
                placeholder="请输入站点名称"
                class="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-4 py-3 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>

            <div>
              <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">品牌名称</label>
              <p class="mb-2 text-xs text-gray-500 dark:text-gray-400">显示在 Logo 旁边的主品牌文字；可与站点名称不同。</p>
              <input
                v-model="form.brandName"
                type="text"
                placeholder="请输入品牌名称"
                class="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-4 py-3 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>

            <div>
              <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">品牌别名</label>
              <p class="mb-2 text-xs text-gray-500 dark:text-gray-400">显示在品牌名称下方的补充文字（比如英文等其他语言），可留空。</p>
              <input
                v-model="form.brandNameAlias"
                type="text"
                placeholder="请输入品牌别名，可留空"
                class="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-4 py-3 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>

            <div>
              <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">浅色 Logo 地址</label>
              <input
                v-model="form.logoLightUrl"
                type="text"
                placeholder="https://example.com/logo-light.png"
                class="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-4 py-3 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
              <TenantLogoUploader
                v-if="isEditMode && editingTenantId"
                :tenant-id="editingTenantId"
                variant="light"
                :current-url="form.logoLightUrl"
                @uploaded="handleLogoUploaded"
              />
            </div>

            <div>
              <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">深色 Logo 地址</label>
              <input
                v-model="form.logoDarkUrl"
                type="text"
                placeholder="https://example.com/logo-dark.png"
                class="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-4 py-3 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
              <TenantLogoUploader
                v-if="isEditMode && editingTenantId"
                :tenant-id="editingTenantId"
                variant="dark"
                :current-url="form.logoDarkUrl"
                @uploaded="handleLogoUploaded"
              />
            </div>

            <div class="md:col-span-2">
              <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">站点图标（Favicon）</label>
              <p class="mb-2 text-xs text-gray-500 dark:text-gray-400">
                用于浏览器标签页与收藏夹图标。上传后会统一裁剪为 128x128 的 PNG，并覆盖当前租户托管图标。
              </p>
              <TenantFaviconUploader
                v-if="isEditMode && editingTenantId"
                :tenant-id="editingTenantId"
                :current-url="form.faviconUrl"
                @uploaded="handleFaviconUploaded"
              />
            </div>
          </div>
        </div>

        <div v-show="activeTab === 'support'">
          <div class="space-y-4">
            <div class="rounded-lg border border-gray-200 bg-gray-50 p-4 dark:border-gray-700 dark:bg-gray-900/40">
              <h3 class="text-base font-semibold text-gray-900 dark:text-gray-100">在线客服</h3>
              <p class="mt-1 text-xs leading-5 text-gray-500 dark:text-gray-400">
                配置当前租户在主站与教师端展示的客服入口。当前阶段仅支持微信对话平台，后续会扩展 Chatwoot、Crisp 等方案。
              </p>

              <div class="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2">
                <div>
                  <label class="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">客服类型</label>
                  <select
                    v-model="form.supportProvider"
                    class="w-full rounded-lg border border-gray-300 bg-white px-4 py-3 text-gray-900 focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100"
                  >
                    <option value="none">使用平台客服</option>
                    <option value="wechat">微信对话平台</option>
                  </select>
                </div>

                <div>
                  <label class="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">客服显示名称</label>
                  <input
                    v-model="form.supportDisplayName"
                    type="text"
                    placeholder="留空时默认使用站点名称"
                    class="w-full rounded-lg border border-gray-300 bg-white px-4 py-3 text-gray-900 focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100"
                    :disabled="form.supportProvider === 'none'"
                  />
                </div>
              </div>

              <div v-if="form.supportProvider === 'none'" class="mt-4 space-y-3">
                <div class="rounded-lg border border-dashed border-gray-300 bg-white p-4 text-sm text-gray-600 dark:border-gray-600 dark:bg-gray-800/60 dark:text-gray-300">
                  当前租户未单独配置客服，将自动回退到平台默认租户的在线客服配置。
                </div>

                <div class="flex flex-wrap items-center gap-3">
                  <button
                    type="button"
                    class="inline-flex items-center rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700"
                    :disabled="previewingSupport || !canPreviewSupport"
                    @click="handlePreviewSupport"
                  >
                    {{ previewingSupport ? '打开中...' : '测试打开平台客服' }}
                  </button>
                  <span class="text-xs text-gray-500 dark:text-gray-400">
                    预览会使用平台默认租户的客服配置。
                  </span>
                </div>
              </div>

              <div v-else class="mt-4 space-y-3">
                <div>
                  <div class="mb-2 flex flex-wrap items-center justify-between gap-2">
                    <label class="block text-sm font-medium text-gray-700 dark:text-gray-300">微信对话平台 Token</label>
                    <a
                      href="https://developers.weixin.qq.com/doc/aispeech/platform/INTRODUCTION.html"
                      target="_blank"
                      rel="noopener noreferrer"
                      class="inline-flex items-center rounded-lg border border-sky-200 bg-sky-50 px-3 py-1.5 text-xs font-medium text-sky-700 transition hover:bg-sky-100 dark:border-sky-800 dark:bg-sky-950/40 dark:text-sky-300 dark:hover:bg-sky-900/40"
                    >
                      查看配置文档
                    </a>
                  </div>
                  <input
                    v-model="form.supportWechatToken"
                    type="text"
                    autocomplete="off"
                    placeholder="请输入微信对话平台 Token"
                    class="w-full rounded-lg border border-gray-300 bg-white px-4 py-3 font-mono text-gray-900 focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100"
                  />
                  <p class="mt-2 text-xs text-gray-500 dark:text-gray-400">
                    用于生成微信对话平台 Web H5 入口链接。{{ form.supportWechatConfigured ? '当前已加载已保存 Token，可直接查看或修改。' : '当前租户尚未配置 Token。' }}
                  </p>
                </div>

                <div class="flex flex-wrap items-center gap-3">
                  <button
                    type="button"
                    class="inline-flex items-center rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700"
                    :disabled="previewingSupport || !canPreviewSupport"
                    @click="handlePreviewSupport"
                  >
                    {{ previewingSupport ? '打开中...' : '测试打开客服' }}
                  </button>
                  <span class="text-xs text-gray-500 dark:text-gray-400">
                    预览会使用当前草稿配置，不需要先保存。
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div v-show="activeTab === 'homepage'">
          <div class="space-y-4">
            <div class="flex items-center justify-between gap-3">
              <h3 class="text-base font-semibold text-gray-900 dark:text-gray-100">主页配置</h3>
              <button
                v-if="form.homepageMode !== 'default'"
                type="button"
                @click="previewHomepage"
                class="inline-flex items-center rounded-lg border border-gray-300 dark:border-gray-600 px-3 py-2 text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
              >
                预览主页
              </button>
            </div>

            <div class="grid grid-cols-1 gap-3 md:grid-cols-3">
              <label
                v-for="option in TENANT_HOMEPAGE_MODE_OPTIONS"
                :key="option.value"
                class="flex items-start gap-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/40 p-4 cursor-pointer"
              >
                <input
                  v-model="form.homepageMode"
                  :value="option.value"
                  type="radio"
                  class="mt-1 h-4 w-4 border-gray-300 text-indigo-600 focus:ring-indigo-500"
                />
                <div>
                  <p class="text-sm font-medium text-gray-900 dark:text-gray-100">{{ option.label }}</p>
                  <p class="mt-1 text-xs text-gray-500 dark:text-gray-400">{{ option.description }}</p>
                </div>
              </label>
            </div>
            <div v-if="form.homepageMode === 'url'">
              <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">外部主页 URL</label>
              <input
                v-model="form.homepageContent"
                type="text"
                placeholder="https://example.com/homepage"
                class="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-4 py-3 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
              <p class="mt-2 text-xs text-gray-500 dark:text-gray-400">
                仅支持 https 地址，前台会以无脚本 iframe 形式嵌入该页面。
              </p>
            </div>

            <div v-else-if="form.homepageMode === 'html'">
              <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">自定义 HTML 内容</label>
              <textarea
                v-model="form.homepageContent"
                rows="10"
                placeholder="请输入仅包含 HTML/CSS 的主页内容"
                class="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-4 py-3 text-gray-900 dark:text-gray-100 font-mono text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
              <p class="mt-2 text-xs text-gray-500 dark:text-gray-400">
                仅支持 HTML/CSS，不允许 script、内联事件、javascript: 链接或嵌套 iframe。
              </p>
            </div>
          </div>
        </div>

        <div v-show="activeTab === 'filings'">
          <div class="space-y-4">
            <div class="rounded-lg border border-gray-200 bg-gray-50 p-4 dark:border-gray-700 dark:bg-gray-900/40">
              <h3 class="text-base font-semibold text-gray-900 dark:text-gray-100">页脚备案</h3>
              <p class="mt-1 text-xs leading-5 text-gray-500 dark:text-gray-400">
                配置当前租户首页展示的备案信息。未配置则不展示；URL 首页模式下不会附加平台页脚。
              </p>

              <div class="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2">
                <div>
                  <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">ICP备案号</label>
                  <input
                    v-model="form.footerIcpNumber"
                    type="text"
                    placeholder="例如：陕ICP备2025072629号"
                    class="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-4 py-3 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                  <p class="mt-2 text-xs text-gray-500 dark:text-gray-400">未配置则不展示。</p>
                </div>

                <div>
                  <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">公安备案号</label>
                  <input
                    v-model="form.footerPsbNumber"
                    type="text"
                    placeholder="例如：陕公网安备61011202001083号"
                    class="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-4 py-3 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                  <p class="mt-2 text-xs text-gray-500 dark:text-gray-400">未配置则不展示。</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div v-show="activeTab === 'filings'">
          <div class="space-y-4">
            <div class="rounded-lg border border-gray-200 bg-gray-50 p-4 dark:border-gray-700 dark:bg-gray-900/40">
              <h3 class="text-base font-semibold text-gray-900 dark:text-gray-100">页脚备案</h3>
              <p class="mt-1 text-xs leading-5 text-gray-500 dark:text-gray-400">
                配置当前租户首页展示的备案信息。未配置则不展示；URL 首页模式下不会附加平台页脚。
              </p>

              <div class="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2">
                <div>
                  <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">ICP备案号</label>
                  <input
                    v-model="form.footerIcpNumber"
                    type="text"
                    placeholder="例如：陕ICP备2025072629号"
                    class="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-4 py-3 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                  <p class="mt-2 text-xs text-gray-500 dark:text-gray-400">未配置则不展示。</p>
                </div>

                <div>
                  <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">公安备案号</label>
                  <input
                    v-model="form.footerPsbNumber"
                    type="text"
                    placeholder="例如：陕公网安备61011202001083号"
                    class="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-4 py-3 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                  <p class="mt-2 text-xs text-gray-500 dark:text-gray-400">未配置则不展示。</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div v-show="activeTab === 'domains'">
          <div class="space-y-4">
            <h3 class="text-base font-semibold text-gray-900 dark:text-gray-100">域名配置</h3>

            <div>
              <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">域名列表</label>
              <textarea
                v-model="form.customDomainsText"
                rows="4"
                placeholder="每行一个完整域名，也支持使用逗号分隔&#10;例如：customer-a.com&#10;www.customer-a.com&#10;foo.magly.cn"
                class="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-4 py-3 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
              <p class="mt-2 text-xs text-amber-600 dark:text-amber-400">
                ⚠️ 请填写完整域名，客户需要将域名解析到服务器
              </p>
              <p class="mt-1 text-xs text-gray-500 dark:text-gray-400">
                支持每行一个域名或使用逗号分隔；提交时会自动去重并转小写
              </p>
            </div>
          </div>
        </div>

        <div v-show="activeTab === 'apps'">
          <div class="grid grid-cols-1 gap-5 md:grid-cols-2">
            <div>
              <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">平台授予应用</label>
              <div class="space-y-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/40 p-4">
                <label
                  v-for="option in TENANT_ENABLED_APP_OPTIONS"
                  :key="option.value"
                  class="flex items-center gap-3 text-sm text-gray-700 dark:text-gray-300 cursor-pointer"
                >
                  <input
                    v-model="form.grantedApps"
                    :value="option.value"
                    type="checkbox"
                    @change="syncEnabledAppsWithinGrant"
                    class="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                  />
                  <span>{{ option.label }}</span>
                </label>
              </div>
              <p class="mt-2 text-xs text-gray-500 dark:text-gray-400">平台授予能力决定租户控制台可开启的范围。</p>
            </div>

            <div>
              <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">当前启用应用</label>
              <div class="space-y-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/40 p-4">
                <label
                  v-for="option in TENANT_ENABLED_APP_OPTIONS"
                  :key="option.value"
                  class="flex items-center gap-3 text-sm text-gray-700 dark:text-gray-300 cursor-pointer"
                >
                  <input
                    v-model="form.enabledApps"
                    :value="option.value"
                    type="checkbox"
                    :disabled="!form.grantedApps.includes(option.value)"
                    class="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                  />
                  <span>{{ option.label }}<span v-if="!form.grantedApps.includes(option.value)" class="text-xs text-gray-400 ml-1">（未授予）</span></span>
                </label>
              </div>
              <p class="mt-2 text-xs text-gray-500 dark:text-gray-400">租户控制台只能在已授予应用中开启或关闭。</p>
            </div>
          </div>

          <div class="grid grid-cols-1 gap-5 md:grid-cols-2 mt-5">
            <div>
              <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">平台授予登录方式</label>
              <div class="space-y-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/40 p-4">
                <label
                  v-for="option in TENANT_LOGIN_METHOD_OPTIONS"
                  :key="option.value"
                  class="flex items-center gap-3 text-sm text-gray-700 dark:text-gray-300 cursor-pointer"
                >
                  <input
                    v-model="form.grantedLoginMethods"
                    :value="option.value"
                    type="checkbox"
                    @change="syncLoginMethodsWithinGrant"
                    class="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                  />
                  <span>{{ option.label }}</span>
                </label>
              </div>
            </div>

            <div>
              <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">当前启用登录方式</label>
              <div class="space-y-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/40 p-4">
                <label
                  v-for="option in TENANT_LOGIN_METHOD_OPTIONS"
                  :key="option.value"
                  class="flex items-center gap-3 text-sm text-gray-700 dark:text-gray-300 cursor-pointer"
                >
                  <input
                    v-model="form.loginMethods"
                    :value="option.value"
                    type="checkbox"
                    :disabled="!form.grantedLoginMethods.includes(option.value)"
                    class="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                  />
                  <span>{{ option.label }}<span v-if="!form.grantedLoginMethods.includes(option.value)" class="text-xs text-gray-400 ml-1">（未授予）</span></span>
                </label>
              </div>
            </div>
          </div>
        </div>

        <div v-show="activeTab === 'status'">
          <div class="grid grid-cols-1 gap-5 md:grid-cols-2">
            <label class="flex items-start gap-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/40 p-4 cursor-pointer">
              <input
                v-model="form.isActive"
                type="checkbox"
                class="mt-1 h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
              />
              <div>
                <p class="text-sm font-medium text-gray-900 dark:text-gray-100">启用租户</p>
                <p class="mt-1 text-xs text-gray-500 dark:text-gray-400">停用后将不会按 Host 命中该租户配置</p>
              </div>
            </label>

            <label class="flex items-start gap-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/40 p-4 cursor-pointer">
              <input
                v-model="form.isDefault"
                type="checkbox"
                class="mt-1 h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
              />
              <div>
                <p class="text-sm font-medium text-gray-900 dark:text-gray-100">设为默认租户</p>
                <p class="mt-1 text-xs text-gray-500 dark:text-gray-400">平台主站域名应绑定到默认租户，默认租户不会自动绑定新用户</p>
              </div>
            </label>
          </div>

          <div class="grid grid-cols-1 gap-5 md:grid-cols-2 mt-5">
            <label class="flex items-start gap-3 rounded-lg border border-amber-200 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/20 p-4 cursor-pointer">
              <input
                v-model="form.isDemo"
                type="checkbox"
                class="mt-1 h-4 w-4 rounded border-gray-300 text-amber-600 focus:ring-amber-500"
              />
              <div>
                <p class="text-sm font-medium text-gray-900 dark:text-gray-100">演示租户</p>
                <p class="mt-1 text-xs text-gray-500 dark:text-gray-400">启用后该租户控制台将使用演示数据，品牌设置仍可真实修改</p>
              </div>
            </label>
          </div>
        </div>
        </div>
        <!-- End of fixed-height wrapper -->

        <div class="flex items-center justify-end gap-3 pt-2">
          <button
            type="button"
            @click="closeTenantModal"
            class="px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
          >
            取消
          </button>
          <button
            type="submit"
            :disabled="submitting"
            class="primary-btn min-w-[120px] justify-center disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {{ submitting ? '保存中...' : isEditMode ? '保存修改' : '创建租户' }}
          </button>
        </div>
      </form>
    </Modal>

    <Modal
      :show="showTeamModal"
      @close="closeTeamModal"
      :title="teamModalTitle"
      size="xlarge"
    >
      <TenantConsoleManager v-if="teamTenant" :tenant-id="teamTenant.id" :tenant-name="teamTenant.name" embedded />
    </Modal>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, reactive, ref } from 'vue'
import { ArrowPathIcon, BuildingOffice2Icon, PencilSquareIcon, PlusIcon, TrashIcon, UserGroupIcon } from '@heroicons/vue/24/outline'
import Modal from '@/components/common/Modal.vue'
import TenantFaviconUploader from '@/components/TenantFaviconUploader.vue'
import Pagination from '@/components/ui/Pagination.vue'
import TenantLogoUploader from '@/components/TenantLogoUploader.vue'
import TenantConsoleManager from '@/views/admin/TenantConsoleManager.vue'
import toaster from '@/utils/toaster'
import {
  createTenantAPI,
  deleteTenantAPI,
  getTenantAPI,
  getTenantsAPI,
  previewTenantSupportLinkAPI,
  updateTenantAPI,
} from '@/api/admin/tenants'
import {
  TENANT_ENABLED_APP_LABELS,
  TENANT_ENABLED_APP_OPTIONS,
  TENANT_HOMEPAGE_MODE_LABELS,
  TENANT_HOMEPAGE_MODE_OPTIONS,
  TENANT_LOGIN_METHOD_LABELS,
  TENANT_LOGIN_METHOD_OPTIONS,
} from '@/types/admin/tenant'
import type {
  AdminTenantConfig,
  CreateTenantRequest,
  TenantSupportProvider,
  TenantEnabledApp,
  TenantFaviconUploadResponse,
  TenantHomepageMode,
  TenantLogoVariant,
  TenantLoginMethod,
  TenantPagination,
  UpdateTenantRequest,
} from '@/types/admin/tenant'

interface TenantFormState {
  name: string
  brandName: string
  brandNameAlias: string
  customDomainsText: string
  logoLightUrl: string
  logoDarkUrl: string
  faviconUrl: string
  supportProvider: TenantSupportProvider
  supportDisplayName: string
  supportWechatConfigured: boolean
  supportWechatToken: string
  grantedApps: TenantEnabledApp[]
  enabledApps: TenantEnabledApp[]
  grantedLoginMethods: TenantLoginMethod[]
  loginMethods: TenantLoginMethod[]
  isActive: boolean
  isDefault: boolean
  isDemo: boolean
  homepageMode: TenantHomepageMode
  homepageContent: string
  footerIcpNumber: string
  footerPsbNumber: string
}

const HOMEPAGE_SCRIPT_PATTERN = /<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi
const HOMEPAGE_INLINE_EVENT_PATTERN = /\son[a-z]+\s*=/gi
const HOMEPAGE_JAVASCRIPT_PROTOCOL_PATTERN = /(?:href|src)\s*=\s*['"]\s*javascript:/gi
const HOMEPAGE_EMBEDDED_FRAME_PATTERN = /<(?:iframe|frame|object|embed)\b/gi
const HOMEPAGE_META_REFRESH_PATTERN = /<meta\b[^>]*http-equiv\s*=\s*['"]?refresh/gi

const loading = ref(false)
const submitting = ref(false)
const showTenantModal = ref(false)
const showTeamModal = ref(false)
const isEditMode = ref(false)
const editingTenantId = ref<number | null>(null)
const previewingSupport = ref(false)
const teamTenant = ref<AdminTenantConfig | null>(null)
const tenants = ref<AdminTenantConfig[]>([])

const tabs = [
  { id: 'basic', label: '基本信息' },
  { id: 'support', label: '在线客服' },
  { id: 'homepage', label: '主页配置' },
  { id: 'filings', label: '页脚备案' },
  { id: 'domains', label: '域名配置' },
  { id: 'apps', label: '应用与登录' },
  { id: 'status', label: '状态设置' },
];
const activeTab = ref('basic');

const pagination = reactive<TenantPagination>({
  page: 1,
  limit: 20,
  total: 0,
})
const form = reactive<TenantFormState>({
  name: '',
  brandName: '',
  brandNameAlias: '',
  customDomainsText: '',
  logoLightUrl: '',
  logoDarkUrl: '',
  faviconUrl: '',
  supportProvider: 'none',
  supportDisplayName: '',
  supportWechatConfigured: false,
  supportWechatToken: '',
  grantedApps: [],
  enabledApps: [],
  grantedLoginMethods: [],
  loginMethods: [],
  isActive: true,
  isDefault: false,
  isDemo: false,
  homepageMode: 'default',
  homepageContent: '',
  footerIcpNumber: '',
  footerPsbNumber: '',
})

const modalTitle = computed(() => (isEditMode.value ? '编辑租户' : '新建租户'))
const teamModalTitle = computed(() => (teamTenant.value ? `${teamTenant.value.name} - 团队与租户用户` : '团队与租户用户'))
const canPreviewSupport = computed(() => {
  if (form.supportProvider === 'none') {
    return true
  }

  return Boolean(form.supportWechatToken.trim() || form.supportWechatConfigured)
})

const activeTenantCount = computed(() => tenants.value.filter((tenant) => tenant.isActive).length)

const defaultTenantName = computed(() => {
  const tenant = tenants.value.find((item) => item.isDefault)
  return tenant?.name || '当前页暂无默认租户'
})

/**
 * 格式化日期时间。
 *
 * @param dateTime 日期时间字符串
 * @returns 格式化结果
 */
const formatDateTime = (dateTime: string) => {
  const date = new Date(dateTime)

  if (Number.isNaN(date.getTime())) {
    return '无效时间'
  }

  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  const hours = String(date.getHours()).padStart(2, '0')
  const minutes = String(date.getMinutes()).padStart(2, '0')

  return `${year}-${month}-${day} ${hours}:${minutes}`
}

/**
 * 重置表单。
 */
const resetForm = () => {
  form.name = ''
  form.brandName = ''
  form.brandNameAlias = ''
  form.customDomainsText = ''
  form.logoLightUrl = ''
  form.logoDarkUrl = ''
  form.faviconUrl = ''
  form.supportProvider = 'none'
  form.supportDisplayName = ''
  form.supportWechatConfigured = false
  form.supportWechatToken = ''
  form.grantedApps = []
  form.enabledApps = []
  form.grantedLoginMethods = []
  form.loginMethods = []
  form.isActive = true
  form.isDefault = false
  form.isDemo = false
  form.homepageMode = 'default'
  form.homepageContent = ''
  form.footerIcpNumber = ''
  form.footerPsbNumber = ''
  previewingSupport.value = false
  editingTenantId.value = null
  isEditMode.value = false
  activeTab.value = 'basic'
}

/**
 * 将租户详情写入表单。
 *
 * @param tenant 租户详情
 */
const fillForm = async (tenant: AdminTenantConfig) => {
  form.name = tenant.name
  form.brandName = tenant.brandName || ''
  form.brandNameAlias = tenant.brandNameAlias || ''
  form.logoLightUrl = tenant.logoLightUrl || ''
  form.logoDarkUrl = tenant.logoDarkUrl || ''
  form.faviconUrl = tenant.faviconUrl || ''
  form.supportProvider = tenant.supportProvider || 'none'
  form.supportDisplayName = tenant.supportDisplayName || ''
  form.supportWechatConfigured = Boolean(tenant.supportWechatConfigured)
  form.supportWechatToken = tenant.supportWechatToken || ''
  form.grantedApps = [...tenant.grantedApps]
  form.enabledApps = [...tenant.enabledApps]
  form.grantedLoginMethods = [...tenant.grantedLoginMethods]
  form.loginMethods = [...tenant.loginMethods]
  form.isActive = tenant.isActive
  form.isDefault = tenant.isDefault
  form.isDemo = tenant.isDemo
  form.homepageMode = tenant.homepageMode || 'default'
  form.homepageContent = tenant.homepageContent || ''
  form.footerIcpNumber = tenant.footerIcpNumber || ''
  form.footerPsbNumber = tenant.footerPsbNumber || ''

  form.customDomainsText = (tenant.hosts || []).map(h => h.host).join('\n')
}

/**
 * 保证启用应用是授予应用的子集。
 */
const syncEnabledAppsWithinGrant = () => {
  form.enabledApps = form.enabledApps.filter((item) => form.grantedApps.includes(item))
}

/**
 * 保证启用登录方式是授予登录方式的子集。
 */
const syncLoginMethodsWithinGrant = () => {
  form.loginMethods = form.loginMethods.filter((item) => form.grantedLoginMethods.includes(item))
}

/**
 * 规范化单个域名。
 *
 * @param host 原始域名
 * @returns 规范化后的域名
 */
const normalizeHost = (host: string) => host.trim().toLowerCase()

/**
 * 解析并规范化域名输入。
 *
 * @param hostsText 域名文本
 * @returns 规范化后的域名列表
 */
const parseHostsText = (hostsText: string) => {
  const normalizedHosts: string[] = []

  hostsText
    .split(/[\n,]/)
    .map((host) => normalizeHost(host))
    .filter(Boolean)
    .forEach((host) => {
      if (!normalizedHosts.includes(host)) {
        normalizedHosts.push(host)
      }
    })

  return normalizedHosts
}

/**
 * 校验主页配置。
 */
const validateHomepageConfig = (homepageMode: TenantHomepageMode, homepageContent: string) => {
  const normalizedContent = homepageContent.trim()

  if (homepageMode === 'default') {
    return { valid: true, content: '', error: null as string | null }
  }

  if (!normalizedContent) {
    return { valid: false, content: '', error: '自定义主页内容不能为空' }
  }

  if (homepageMode === 'url') {
    if (!normalizedContent.startsWith('https://')) {
      return { valid: false, content: '', error: 'URL 模式仅支持 https 地址' }
    }

    return { valid: true, content: normalizedContent, error: null as string | null }
  }

  if (HOMEPAGE_SCRIPT_PATTERN.test(normalizedContent)) {
    HOMEPAGE_SCRIPT_PATTERN.lastIndex = 0
    return { valid: false, content: '', error: 'HTML 模式不允许包含 script 脚本' }
  }
  HOMEPAGE_SCRIPT_PATTERN.lastIndex = 0

  if (HOMEPAGE_INLINE_EVENT_PATTERN.test(normalizedContent)) {
    HOMEPAGE_INLINE_EVENT_PATTERN.lastIndex = 0
    return { valid: false, content: '', error: 'HTML 模式不允许包含内联事件处理器' }
  }
  HOMEPAGE_INLINE_EVENT_PATTERN.lastIndex = 0

  if (HOMEPAGE_JAVASCRIPT_PROTOCOL_PATTERN.test(normalizedContent)) {
    HOMEPAGE_JAVASCRIPT_PROTOCOL_PATTERN.lastIndex = 0
    return { valid: false, content: '', error: 'HTML 模式不允许使用 javascript: 协议' }
  }
  HOMEPAGE_JAVASCRIPT_PROTOCOL_PATTERN.lastIndex = 0

  if (HOMEPAGE_EMBEDDED_FRAME_PATTERN.test(normalizedContent)) {
    HOMEPAGE_EMBEDDED_FRAME_PATTERN.lastIndex = 0
    return { valid: false, content: '', error: 'HTML 模式不允许嵌套 iframe、object 或 embed' }
  }
  HOMEPAGE_EMBEDDED_FRAME_PATTERN.lastIndex = 0

  if (HOMEPAGE_META_REFRESH_PATTERN.test(normalizedContent)) {
    HOMEPAGE_META_REFRESH_PATTERN.lastIndex = 0
    return { valid: false, content: '', error: 'HTML 模式不允许使用 meta refresh' }
  }
  HOMEPAGE_META_REFRESH_PATTERN.lastIndex = 0

  return { valid: true, content: normalizedContent, error: null as string | null }
}

/**
 * 生成 HTML 模式预览文档。
 */
const buildHomepagePreviewDocument = (htmlContent: string) => `<!DOCTYPE html>
<html lang="zh-CN">
<head>
  <meta charset="UTF-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1.0" />
  <title>主页预览</title>
  <style>
    html, body { margin: 0; padding: 0; height: 100%; background: #f3f4f6; }
    iframe { width: 100%; min-height: 100vh; border: 0; background: #fff; }
  </style>
</head>
<body>
  <iframe sandbox="allow-forms allow-popups allow-popups-to-escape-sandbox" referrerpolicy="no-referrer" srcdoc=${JSON.stringify(htmlContent)}></iframe>
</body>
</html>`

/**
 * 预览主页配置。
 */
const previewHomepage = () => {
  const validation = validateHomepageConfig(form.homepageMode, form.homepageContent)
  if (!validation.valid) {
    toaster.error(validation.error || '主页配置无效')
    return
  }

  if (form.homepageMode === 'default') {
    toaster.info('默认主页无需预览')
    return
  }

  if (form.homepageMode === 'url') {
    window.open(validation.content, '_blank', 'noopener,noreferrer')
    return
  }

  const previewWindow = window.open('', '_blank', 'noopener,noreferrer')
  if (!previewWindow) {
    toaster.error('浏览器阻止了预览窗口，请允许弹窗后重试')
    return
  }

  previewWindow.document.open()
  previewWindow.document.write(buildHomepagePreviewDocument(validation.content))
  previewWindow.document.close()
}

/**
 * 处理 Logo 上传成功回调。
 */
const handleLogoUploaded = (payload: { variant: TenantLogoVariant; logoUrl: string }) => {
  if (payload.variant === 'light') {
    form.logoLightUrl = payload.logoUrl
    return
  }

  form.logoDarkUrl = payload.logoUrl
}

/**
 * 处理 Favicon 上传成功回调。
 */
const handleFaviconUploaded = (payload: TenantFaviconUploadResponse) => {
  form.faviconUrl = payload.faviconUrl
}

/**
 * 预览当前草稿在线客服链接。
 */
const handlePreviewSupport = async () => {
  if (previewingSupport.value || !canPreviewSupport.value) {
    return
  }

  const pendingWindow = window.open('', '_blank')
  if (!pendingWindow) {
    toaster.error('浏览器拦截了新窗口，请允许弹窗后重试')
    return
  }

  pendingWindow.opener = null
  pendingWindow.document.title = '正在打开客服...'
  pendingWindow.document.body.innerHTML = '<div style="font-family: -apple-system, BlinkMacSystemFont, \"Segoe UI\", sans-serif; padding: 24px; color: #374151;">正在打开客服...</div>'

  previewingSupport.value = true
  try {
    const response = await previewTenantSupportLinkAPI({
      tenantId: editingTenantId.value || undefined,
      isDefault: form.isDefault,
      tenantName: form.name.trim(),
      supportProvider: form.supportProvider,
      supportDisplayName: form.supportDisplayName.trim(),
      supportWechatToken: form.supportWechatToken.trim() || undefined,
    })

    if (response.status !== 200 || !response.data?.url) {
      pendingWindow.close()
      toaster.error(response.message || '获取客服链接失败')
      return
    }

    pendingWindow.location.href = response.data.url
  } catch (error) {
    pendingWindow.close()
    console.error('Preview support error:', error)
    toaster.error('获取客服链接失败')
  } finally {
    previewingSupport.value = false
  }
}

/**
 * 构造保存请求数据。
 *
 * @returns 请求数据
 */
const buildTenantPayload = (): CreateTenantRequest | UpdateTenantRequest => {
  const name = form.name.trim()

  if (!name) {
    throw new Error('请输入站点名称')
  }

  if (form.isDefault && !form.isActive) {
    throw new Error('默认租户必须保持启用状态')
  }

  const basePayload = {
    name,
    brandName: form.brandName.trim(),
    brandNameAlias: form.brandNameAlias.trim(),
    logoLightUrl: form.logoLightUrl.trim(),
    logoDarkUrl: form.logoDarkUrl.trim(),
    supportProvider: form.supportProvider,
    supportDisplayName: form.supportDisplayName.trim(),
    supportWechatToken: form.supportWechatToken.trim() || undefined,
    grantedApps: [...form.grantedApps],
    enabledApps: [...form.enabledApps],
    grantedLoginMethods: [...form.grantedLoginMethods],
    loginMethods: [...form.loginMethods],
    isActive: form.isActive,
    isDefault: form.isDefault,
    isDemo: form.isDemo,
    homepageMode: 'default' as TenantHomepageMode,
    homepageContent: '',
    footerIcpNumber: form.footerIcpNumber.trim(),
    footerPsbNumber: form.footerPsbNumber.trim(),
  }

  const homepageValidation = validateHomepageConfig(form.homepageMode, form.homepageContent)
  if (!homepageValidation.valid) {
    throw new Error(homepageValidation.error || '主页配置无效')
  }

  basePayload.homepageMode = form.homepageMode
  basePayload.homepageContent = homepageValidation.content

  if (!isEditMode.value) {
    const customDomains = parseHostsText(form.customDomainsText)

    if (customDomains.length === 0) {
      throw new Error('请至少配置一个域名')
    }

    return {
      ...basePayload,
      customDomains,
    } as CreateTenantRequest
  }

  const customDomains = parseHostsText(form.customDomainsText)

  if (customDomains.length === 0) {
    throw new Error('请至少配置一个域名')
  }

  return {
    ...basePayload,
    customDomains,
  } as UpdateTenantRequest
}

/**
 * 加载租户列表。
 */
const loadTenants = async () => {
  loading.value = true

  try {
    const response = await getTenantsAPI({
      page: pagination.page,
      limit: pagination.limit,
    })

    if (response.status === 200 && response.data) {
      tenants.value = response.data.tenants || []
      pagination.total = response.data.total || 0
      pagination.page = response.data.page || pagination.page
      pagination.limit = response.data.limit || pagination.limit
      return
    }

    toaster.error(response.message || '加载租户列表失败')
  } catch (error) {
    console.error('Load tenants error:', error)
    toaster.error('加载租户列表失败')
  } finally {
    loading.value = false
  }
}

/**
 * 打开新建租户弹窗。
 */
const handleCreateTenant = () => {
  resetForm()
  showTenantModal.value = true
}

/**
 * 打开编辑租户弹窗。
 *
 * @param tenantId 租户 ID
 */
const handleEditTenant = async (tenantId: number) => {
  submitting.value = true

  try {
    const response = await getTenantAPI(tenantId)

    if (response.status === 200 && response.data) {
      resetForm()
      await fillForm(response.data)
      editingTenantId.value = tenantId
      isEditMode.value = true
      showTenantModal.value = true
      return
    }

    toaster.error(response.message || '加载租户详情失败')
  } catch (error) {
    console.error('Load tenant detail error:', error)
    toaster.error('加载租户详情失败')
  } finally {
    submitting.value = false
  }
}

/**
 * 关闭租户弹窗。
 */
const closeTenantModal = () => {
  if (submitting.value) {
    return
  }

  showTenantModal.value = false
  resetForm()
}

/**
 * 打开租户团队与用户管理弹窗。
 */
const openTeamModal = (tenant: AdminTenantConfig) => {
  teamTenant.value = tenant
  showTeamModal.value = true
}

/**
 * 关闭团队弹窗。
 */
const closeTeamModal = () => {
  showTeamModal.value = false
  teamTenant.value = null
}

/**
 * 提交租户表单。
 */
const handleSubmitTenant = async () => {
  if (submitting.value) {
    return
  }

  await submitTenant()
}

/**
 * 实际提交租户数据。
 */
const submitTenant = async () => {
  try {
    const payload = buildTenantPayload()
    submitting.value = true

    const response = isEditMode.value && editingTenantId.value !== null
      ? await updateTenantAPI(editingTenantId.value, payload as UpdateTenantRequest)
      : await createTenantAPI(payload as CreateTenantRequest)

    if (response.status === 200 && response.data) {
      toaster.success(isEditMode.value ? '租户更新成功' : '租户创建成功')
      showTenantModal.value = false
      resetForm()
      await loadTenants()
      return
    }

    toaster.error(response.message || '保存租户失败')
  } catch (error) {
    console.error('Save tenant error:', error)
    toaster.error(error instanceof Error ? error.message : '保存租户失败')
  } finally {
    submitting.value = false
  }
}

/**
 * 删除租户。
 *
 * @param tenant 租户信息
 */
const handleDeleteTenant = async (tenant: AdminTenantConfig) => {
  if (tenant.isDefault) {
    toaster.error('默认租户不能删除')
    return
  }

  if (!confirm(`确定要删除租户“${tenant.name}”吗？删除后将无法恢复。`)) {
    return
  }

  submitting.value = true

  try {
    const response = await deleteTenantAPI(tenant.id)

    if (response.status === 200) {
      toaster.success('租户删除成功')

      if (tenants.value.length === 1 && pagination.page > 1) {
        pagination.page -= 1
      }

      await loadTenants()
      return
    }

    toaster.error(response.message || '删除租户失败')
  } catch (error) {
    console.error('Delete tenant error:', error)
    toaster.error('删除租户失败')
  } finally {
    submitting.value = false
  }
}

onMounted(() => {
  loadTenants()
})
</script>
